import type { ReactNode } from "react";
import { forwardRef } from "react";
import { useFilter } from "react-aria";
import { ComboBox as ReactAriaComboBox, Popover as ReactAriaPopover } from "react-aria-components";
import type { Key, ComboBoxProps as ReactAriaComboBoxProps } from "react-aria-components";
import { FieldError } from "react-hook-form";

import { useI18n } from "../use-i18n";
import { cn, type VariantProps } from "../utils";
import { NcButton, ncButtonClasses } from "./nc-button";
import { NcFieldLayout, NcFieldLayoutProps } from "./nc-field-layout";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcIconChevronDown, NcIconCross } from "./nc-icons";
import { NcInput, ncInputWidthVariants } from "./nc-input";
import { NcLabel } from "./nc-label";
import { NcListBox } from "./nc-list-box";
import { NcLoadingIndicator } from "./nc-loading-indicator";
import { ncPopoverClasses } from "./nc-popover";

const EmptyState = () => {
  const { t } = useI18n();
  return <NcFormattedMessage variant="secondary">{t("No items found")}</NcFormattedMessage>;
};

export type NcComboBoxItem = {
  id: number | string;
} & Record<string, Key | undefined>;

export interface NcComboBoxProps
  extends VariantProps<typeof ncInputWidthVariants>, ReactAriaComboBoxProps<NcComboBoxItem> {
  label: string;
  hideLabel?: boolean;
  description?: string;
  variant?: NcFieldLayoutProps["variant"];
  defaultItems?: Iterable<NcComboBoxItem>;
  items?: Iterable<NcComboBoxItem>;
  placeholder?: string;
  isLoading?: boolean;
  renderEmptyState?: () => ReactNode;
  isOpen?: boolean;
  onClearSelection?: () => void;
  error?: FieldError;
}

const actionButtonClasses =
  "absolute top-px right-px bottom-px rounded-sm p-2.5 focus-visible:outline-active focus-visible:outline-2 focus-visible:outline-offset-2";

export const NcComboBox = forwardRef<HTMLInputElement, NcComboBoxProps>(
  (
    {
      label,
      hideLabel,
      description,
      items,
      defaultItems,
      defaultFilter,
      placeholder,
      renderEmptyState,
      isLoading,
      children,
      inputWidth = "auto",
      variant,
      isOpen,
      value,
      onClearSelection,
      error,
      isRequired,
      className,
      ...props
    },
    ref
  ) => {
    const { t } = useI18n();
    const { contains } = useFilter({ sensitivity: "base" });
    const itemFilter = (textValue: string, inputValue: string) => contains(textValue, inputValue);

    const showOpenAction = !onClearSelection || !value;

    return (
      <ReactAriaComboBox
        data-nc="NcComboBox"
        className={cn("relative", className)}
        items={items}
        defaultItems={defaultItems}
        defaultFilter={defaultFilter ?? itemFilter}
        allowsEmptyCollection
        value={value}
        {...props}
      >
        <NcFieldLayout variant={variant}>
          <NcLabel isRequired={isRequired} hideLabel={hideLabel}>
            {label}
          </NcLabel>
          <div className={cn("relative flex", ncInputWidthVariants({ inputWidth }))}>
            <NcInput
              className="pr-8"
              placeholder={placeholder}
              aria-busy={isLoading}
              inputWidth={inputWidth}
              ref={ref}
            />
            {showOpenAction ? (
              <NcButton variant="icon" className={actionButtonClasses} aria-label={t("menu_open")}>
                <NcIconChevronDown aria-hidden="true" />
              </NcButton>
            ) : (
              <button
                className={cn(
                  ncButtonClasses.base,
                  ncButtonClasses.variant.icon,
                  actionButtonClasses
                )}
                aria-label={t("clear_search")}
                onClick={onClearSelection}
              >
                <NcIconCross aria-hidden="true" className="h-3.5" />
              </button>
            )}
            <NcLoadingIndicator
              className={cn(
                "absolute top-px right-px bottom-px rounded-sm px-2 py-2.5",
                "bg-light pointer-events-none transition-opacity duration-200 ease-in-out",
                isLoading ? "opacity-100" : "opacity-0"
              )}
              aria-hidden="true"
            />
          </div>
          <ReactAriaPopover
            className={cn(
              ncPopoverClasses.popover,
              "max-h-[40vh]! min-w-(--trigger-width) overflow-y-auto"
            )}
            isOpen={isOpen}
          >
            {children ? (
              <>{children}</>
            ) : (
              <NcListBox
                renderEmptyState={renderEmptyState ? renderEmptyState : () => <EmptyState />}
                className="max-h-[40vh]"
              >
                {(item: NcComboBoxItem) => (
                  <NcListBox.Item id={item.id} textValue={`${item.label}`}>
                    {item.label}
                  </NcListBox.Item>
                )}
              </NcListBox>
            )}
          </ReactAriaPopover>
          {description && <NcFieldLayout.Description>{description}</NcFieldLayout.Description>}
          <NcFieldLayout.ErrorMessage>{error?.message}</NcFieldLayout.ErrorMessage>
        </NcFieldLayout>
      </ReactAriaComboBox>
    );
  }
);
